import React, { useEffect, useState, useCallback, useRef } from "react";
import { withTranslation } from "react-i18next";
import _ from "lodash";
import { Form, Select, Typography, Space, Popover, Table, Spin } from "antd";
import { QuestionCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { searchPhysiciansAPI } from "../../api/api";
import styled from "styled-components";
import "./PhysicianStyle.less";

const { Text } = Typography;
const { Option } = Select;

const DEBOUNCE_MS = 400;
const MIN_SEARCH_CHARS = 3;

const StyledFormItem = styled(Form.Item)`
  ${(props) => {
    if (props.style?.display) {
      return `.ant-row {
      display: ${props.style.display};
    }`;
    }
  }}
`;

const SingleSelectWrapper = styled(Select)`
  width: 250px;
  .ant-select-selection-item {
    height: inherit;
    font-size: 14px;
    .subText {
      display: none;
    }
  }
`;

const getPhysicianAddress = (physician) => {
  return physician ? (
    <Text className="subText">
      {physician.address ? physician.address + ", " : ""}
      {physician.suiteNumber ? "#" + physician.suiteNumber + ", " : ""}
      {physician.city ? physician.city + ", " : ""}
      {physician.state ? physician.state + ", " : ""}
      {physician.zipCode ? physician.zipCode + (physician.email ? ", " : "") : ""}
      {physician.email || ""}
    </Text>
  ) : (
    ""
  );
};

const PhysicianFormItemComponent = ({
  t,
  label,
  placeholder,
  style,
  defaultValue,
  editable = true,
  strong = false,
  required = false,
  dataTMId,
  onPhysicianSelected = () => {}
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedValue, setSelectedValue] = useState(undefined);

  useEffect(() => {
    if (defaultValue?.id) {
      setSelectedValue({ value: String(defaultValue.id), label: defaultValue.name || "" });
      onPhysicianSelected(defaultValue);
    }
  }, [defaultValue, onPhysicianSelected]);

  const debouncedSearch = useRef(
    _.debounce(async (query) => {
      if (query.length < MIN_SEARCH_CHARS) {
        setSearchResults([]);
        setIsFetching(false);
        return;
      }
      try {
        const response = await searchPhysiciansAPI(query);
        setSearchResults(response?.data?.providers || []);
      } catch (error) {
        setSearchResults([]);
      } finally {
        setIsFetching(false);
      }
    }, DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
      if (value.length < MIN_SEARCH_CHARS) {
        setSearchResults([]);
        setIsFetching(false);
        return;
      }
      setIsFetching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const getNotFoundContent = () => {
    if (searchText.length < MIN_SEARCH_CHARS) return t("type_to_search");
    if (isFetching) return <Spin size="small" indicator={<LoadingOutlined spin />} />;
    return undefined;
  };

  return (
    <StyledFormItem
      name="physician"
      colon={false}
      required={false}
      label={
        label ? (
          <Space>
            <Text strong={strong} style={{ color: "#9B9B9B" }}>
              {label}
            </Text>
            <Popover
              id="physician-popover"
              content={
                <Table
                  dataSource={[defaultValue]}
                  columns={[
                    {
                      title: t("form.physician.popover.npi"),
                      dataIndex: "npi",
                      key: "npi",
                      render: (text) => text || "--"
                    },
                    {
                      title: t("form.physician.popover.dea"),
                      dataIndex: "dea",
                      key: "dea",
                      render: (text) => text || "--"
                    },
                    {
                      title: t("form.physician.popover.stateLisence"),
                      dataIndex: "stateLisence",
                      key: "stateLisence",
                      render: (text) => text || "--"
                    },
                    {
                      title: t("form.physician.popover.phone"),
                      dataIndex: "phone",
                      key: "phone",
                      render: (text) => text || "--"
                    },
                    {
                      title: t("form.physician.popover.fax"),
                      dataIndex: "fax",
                      key: "fax",
                      render: (text) => text || "--"
                    },
                    {
                      title: t("form.physician.popover.address"),
                      dataIndex: "address",
                      key: "address",
                      render: (text, record) => {
                        if (!record) return null;
                        const { address, suiteNumber, city, state, zipCode } = record;
                        return (
                          <div style={{ maxWidth: "350px" }}>
                            {[address, suiteNumber, city, state, zipCode].filter((x) => x).join(", ")}
                          </div>
                        );
                      }
                    },
                    {
                      title: t("form.physician.popover.email"),
                      dataIndex: "email",
                      key: "email",
                      render: (text) => text || "--"
                    }
                  ]}
                  pagination={false}
                />
              }
              trigger="click"
            >
              <QuestionCircleOutlined id="physician-tooltip-icon" style={{ padding: "3px" }} />
            </Popover>
          </Space>
        ) : (
          ""
        )
      }
      rules={[{ required, message: t("form.physician.error_required") }]}
      style={style}
    >
      {editable ? (
        <SingleSelectWrapper
          id="physician"
          allowClear={true}
          value={selectedValue?.value}
          placeholder={placeholder}
          showSearch={true}
          filterOption={false}
          onSearch={handleSearch}
          onSelect={(value) => {
            setSearchText("");
            // Don't clear searchResults - keep them so the label can be displayed
            const selectedProvider = searchResults.find((p) => String(p.id) === String(value));
            if (selectedProvider) {
              setSelectedValue({ value: String(selectedProvider.id), label: selectedProvider.name });
              onPhysicianSelected(selectedProvider);
            }
          }}
          onClear={() => {
            setSelectedValue(undefined);
            setSearchResults([]);
            setSearchText("");
          }}
          notFoundContent={getNotFoundContent()}
          popupClassName="physicians-list"
          optionLabelProp="label"
        >
          {(selectedValue && !searchResults.find((p) => String(p.id) === selectedValue.value)
            ? [{ id: selectedValue.value, name: selectedValue.label }]
            : []
          )
            .concat(searchResults)
            .map((physician) => (
              <Option
                key={String(physician.id)}
                value={String(physician.id)}
                label={physician.name}
                className="physician-option"
              >
                <h4 className="title">{physician.name}</h4>
                {getPhysicianAddress(physician)}
              </Option>
            ))}
        </SingleSelectWrapper>
      ) : (
        <Text data-tm-id={dataTMId}>{defaultValue?.name || t("form.physician.placeholder")}</Text>
      )}
    </StyledFormItem>
  );
};

const PhysicianFormItem = withTranslation()(PhysicianFormItemComponent);

export { PhysicianFormItem };
